import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowLeft, PaperPlaneRight, Checks, Check, ChatsCircle } from "@phosphor-icons/react";
import { api, formatApiError, POS_TZ } from "../lib/api";
import { toast } from "sonner";
import { InitialsBadge } from "./InitialsBadge";
import { TicketChip } from "./InboxThread";

export const activeChat = { personId: null };

const time = (iso) => new Date(iso).toLocaleTimeString("en-AU", { timeZone: POS_TZ, hour: "numeric", minute: "2-digit" });
const dayKey = (iso) => new Date(iso).toLocaleDateString("en-CA", { timeZone: POS_TZ });
function dayLabel(iso) {
  const k = dayKey(iso), now = Date.now();
  if (k === dayKey(now)) return "Today";
  if (k === dayKey(now - 86400000)) return "Yesterday";
  return new Date(iso).toLocaleDateString("en-AU", { timeZone: POS_TZ, weekday: "short", day: "numeric", month: "short" });
}
const shortWhen = (iso) => (dayKey(iso) === dayKey(Date.now()) ? time(iso) : dayLabel(iso));

function flatten(messages) {
  const out = [];
  messages.forEach((m) => {
    out.push({ id: m.id, from_id: m.from_id, text: m.text, at: m.created_at, root: m, isRoot: true });
    (m.replies || []).forEach((r) => out.push({ id: r.id, from_id: r.from_id, text: r.text, at: r.created_at, root: m }));
  });
  return out.sort((a, b) => a.at.localeCompare(b.at));
}

export function ChatList({ data, me, staff, activeId, onOpen }) {
  const byPerson = {};
  [...data.received, ...data.sent].forEach((m) => {
    const other = m.from_id === me ? m.to_id : m.from_id;
    const name = m.from_id === me ? m.to_name : m.from_name;
    const p = byPerson[other] || (byPerson[other] = { id: other, name, last: null, unread: 0 });
    const lastReply = (m.replies || []).slice(-1)[0];
    const lastAt = lastReply?.created_at || m.created_at;
    if (!p.last || lastAt > p.last.at) p.last = { at: lastAt, text: lastReply?.text || m.text, from_me: (lastReply?.from_id || m.from_id) === me };
    if ((m.unread_for || []).includes(me)) p.unread += 1;
  });
  const people = Object.values(byPerson).sort((a, b) => b.last.at.localeCompare(a.last.at));
  if (!people.length) return <div className="px-4 py-10 text-[12.5px] text-slate-500 text-center" data-testid="inbox-empty">No chats yet.<br />Start one with New message.</div>;
  return people.map((p) => (
    <button key={p.id} onClick={() => onOpen(p)} data-testid={`chat-person-${p.id}`}
      className={`w-full text-left px-3 py-2.5 rounded-xl flex items-center gap-3 transition-colors ${activeId === p.id ? "bg-[#EFF6FF]" : "hover:bg-slate-50"}`}>
      <InitialsBadge name={p.name} size={40} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className={`text-[13.5px] truncate ${p.unread ? "font-bold text-slate-900" : "font-semibold text-slate-800"}`}>{p.name}</span>
          <span className={`ml-auto text-[10.5px] shrink-0 ${p.unread ? "text-[#1E5FEB] font-semibold" : "text-slate-400"}`}>{shortWhen(p.last.at)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`flex-1 text-[12px] truncate ${p.unread ? "text-slate-800" : "text-slate-500"}`}>{p.last.from_me ? "You: " : ""}{p.last.text}</span>
          {p.unread > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#1E5FEB] text-white text-[10px] font-bold flex items-center justify-center" data-testid={`chat-unread-${p.id}`}>{p.unread}</span>}
        </div>
        {staff[p.id]?.store_name && <div className="text-[10.5px] text-slate-400 truncate">{staff[p.id].store_name}</div>}
      </div>
    </button>
  ));
}

function Bubble({ it, mine, first, last, seenLabel }) {
  const corner = mine ? (last ? "rounded-br-md" : "") : (last ? "rounded-bl-md" : "");
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"} ${first ? "mt-3" : "mt-0.5"}`} data-testid={`chat-bubble-${it.id}`}>
      <div className={`max-w-[70%] flex flex-col ${mine ? "items-end" : "items-start"}`}>
        <div title={new Date(it.at).toLocaleString("en-AU")}
          className={`px-3.5 py-2 rounded-2xl ${corner} text-[13.5px] leading-snug whitespace-pre-wrap break-words ${mine ? "bg-[#1E5FEB] text-white" : "bg-white text-slate-900 border border-[#E5E7EB]"}`}>
          {it.text}
          {it.isRoot && it.root.ticket_id && <div className="mt-1.5"><TicketChip id={it.root.ticket_id} number={it.root.ticket_number} customer={it.root.customer_name} /></div>}
        </div>
        {last && (
          <div className="mt-1 px-1 text-[10.5px] text-slate-400 flex items-center gap-1">
            {time(it.at)}
            {seenLabel && <span className={`inline-flex items-center gap-0.5 font-semibold ${seenLabel === "Seen" ? "text-[#1E5FEB]" : ""}`} data-testid={`chat-seen-${it.id}`}>
              · {seenLabel === "Seen" ? <Checks size={12} weight="bold" /> : <Check size={12} weight="bold" />}{seenLabel}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function Composer({ name, value, onChange, onSend, busy }) {
  const ref = useRef(null);
  useLayoutEffect(() => { const el = ref.current; if (el) { el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, 120)}px`; } }, [value]);
  return (
    <div className="p-3 border-t border-[#EEF2F6] bg-white">
      <div className="flex items-end gap-2 rounded-[22px] border border-[#E5E7EB] bg-[#F8FAFC] pl-4 pr-1.5 py-1.5 focus-within:bg-white focus-within:border-[#BFDBFE] focus-within:ring-4 focus-within:ring-[#EFF6FF] transition-colors">
        <textarea ref={ref} rows={1} value={value} onChange={(e) => onChange(e.target.value)} placeholder={`Message ${name.split(" ")[0]}…`}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); } }} data-testid="chat-input"
          className="flex-1 resize-none bg-transparent border-0 shadow-none p-0 py-1.5 text-[13.5px] leading-snug text-slate-900 placeholder:text-slate-400 outline-none focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:shadow-none focus:border-0" />
        <button onClick={onSend} disabled={busy || !value.trim()} data-testid="chat-send" title="Send (Enter)"
          className="w-9 h-9 shrink-0 rounded-full bg-[#1E5FEB] hover:bg-[#1849C8] disabled:bg-slate-200 disabled:text-slate-400 text-white flex items-center justify-center transition-[background-color,transform] active:scale-95">
          <PaperPlaneRight size={15} weight="fill" />
        </button>
      </div>
      <div className="mt-1 px-4 text-[10px] text-slate-400">Enter to send · Shift + Enter for a new line</div>
    </div>
  );
}

export function ChatEmpty() {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6 bg-[#FAFBFC]" data-testid="chat-empty">
      <span className="w-14 h-14 rounded-2xl bg-[#EFF6FF] flex items-center justify-center mb-3"><ChatsCircle size={28} weight="duotone" className="text-[#1E5FEB]" /></span>
      <div className="text-[14px] font-semibold text-slate-800">Pick a chat</div>
      <div className="text-[12px] text-slate-500 mt-0.5">Choose a teammate on the left to see your messages.</div>
    </div>
  );
}

export function ChatView({ person, me, store, onBack, onChanged }) {
  const [conv, setConv] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const lastPing = useRef(0);
  const count = useRef(0);

  useEffect(() => {
    let alive = true;
    setConv(null); count.current = 0;
    const pull = (first) => api.get(`/inbox/conversation/${person.id}`).then((r) => { if (alive) { setConv(r.data); if (first) onChanged?.(); } }).catch(() => {});
    pull(true);
    activeChat.personId = person.id;
    const t = setInterval(() => pull(false), 2000);
    return () => { alive = false; clearInterval(t); activeChat.personId = null; onChanged?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person.id]);

  const items = conv ? flatten(conv.messages) : [];
  const latest = conv?.messages.slice(-1)[0];
  const otherSeen = latest ? (latest.seen_at || {})[person.id] : null;
  const typedAt = latest ? (latest.typing || {})[person.id] : null;
  const typing = typedAt && conv && new Date(conv.server_now) - new Date(typedAt) < 6000;
  const lastMine = [...items].reverse().find((it) => it.from_id === me);

  useEffect(() => {
    const el = scrollRef.current;
    if (el && (items.length !== count.current || typing)) { count.current = items.length; el.scrollTo({ top: el.scrollHeight, behavior: "smooth" }); }
  }, [items.length, typing]);

  const onType = (v) => {
    setText(v);
    if (v && latest && Date.now() - lastPing.current > 2500) { lastPing.current = Date.now(); api.post(`/inbox/messages/${latest.id}/typing`).catch(() => {}); }
  };
  const send = async () => {
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      if (latest) await api.post(`/inbox/messages/${latest.id}/reply`, { text: body });
      else await api.post("/inbox/messages", { to_id: person.id, text: body, kind: "message" });
      setText("");
      const r = await api.get(`/inbox/conversation/${person.id}`);
      setConv(r.data);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="h-full flex flex-col min-h-0" data-testid={`chat-view-${person.id}`}>
      <div className="h-[60px] shrink-0 px-4 flex items-center gap-3 border-b border-[#EEF2F6] bg-white">
        <button onClick={onBack} className="md:hidden w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100" data-testid="chat-back"><ArrowLeft size={16} weight="bold" /></button>
        <InitialsBadge name={person.name} size={36} />
        <div className="min-w-0">
          <div className="text-[14px] font-bold text-slate-900 truncate">{person.name}</div>
          <div className={`text-[11.5px] truncate ${typing ? "text-[#1E5FEB] font-semibold" : "text-slate-500"}`} data-testid="chat-header-status">{typing ? "typing…" : store || "Team chat"}</div>
        </div>
      </div>
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-5 pb-4 bg-[#F7F9FC]" data-testid="chat-history">
        {!conv && <div className="text-[13px] text-slate-400 text-center py-10">Loading…</div>}
        {conv && !items.length && <div className="text-[12.5px] text-slate-500 text-center py-10">Say hi to {person.name.split(" ")[0]}</div>}
        {items.map((it, i) => {
          const prev = items[i - 1], next = items[i + 1];
          const newDay = !prev || dayKey(prev.at) !== dayKey(it.at);
          const near = (a, b) => a && b && a.from_id === b.from_id && dayKey(a.at) === dayKey(b.at) && new Date(b.at) - new Date(a.at) < 5 * 60000;
          const mine = it.from_id === me;
          const seenLabel = mine && it.id === lastMine?.id ? (otherSeen && otherSeen >= it.at ? "Seen" : "Sent") : null;
          return (
            <div key={it.id}>
              {newDay && <div className="flex justify-center my-4"><span className="px-3 py-1 rounded-full bg-white border border-[#E5E7EB] text-[10.5px] font-semibold text-slate-500" data-testid="chat-day-divider">{dayLabel(it.at)}</span></div>}
              <Bubble it={it} mine={mine} first={newDay || !near(prev, it)} last={!near(it, next) || !!seenLabel} seenLabel={seenLabel} />
            </div>
          );
        })}
        {typing && (
          <div className="flex justify-start mt-3" data-testid="chat-typing">
            <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-white border border-[#E5E7EB] flex gap-1">
              {[0, 1, 2].map((d) => <span key={d} className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: `${d * 150}ms` }} />)}
            </div>
          </div>
        )}
      </div>
      <Composer name={person.name} value={text} onChange={onType} onSend={send} busy={busy} />
    </div>
  );
}
